import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import { Button } from '@/components/ui-kit/button';
import { Checkbox } from '@/components/ui-kit/checkbox';
import { Input } from '@/components/ui-kit/input';
import { Label } from '@/components/ui-kit/label';
import { Textarea } from '@/components/ui-kit/textarea';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/state/store/auth';
import { useToast } from '@/hooks/use-toast';
import { useCreateCompany, useUpdateCompany } from '../hooks/use-companies';
import { isValidWebsite } from '../lib/website-url';
import type {
  Company,
  CompanyDraft,
  CompanyInsertInput,
  CompanyUpdateInput,
} from '../types/companies.types';

/** Basic, permissive validators — only Name is required, so the rest run on non-empty values. */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^[+]?[\d\s().-]{6,}$/;

interface FieldDef {
  key: 'name' | 'website' | 'email' | 'phone' | 'address';
  labelKey: string;
  hintKey: string;
  placeholderKey: string;
  required?: boolean;
  type?: string;
  /** Grid columns spanned (1 or 2). */
  span?: 1 | 2;
  multiline?: boolean;
}

const FIELD_DEFS: FieldDef[] = [
  {
    key: 'name',
    labelKey: 'ORGCOMPANIES_FIELD_NAME',
    hintKey: 'ORGCOMPANIES_HINT_NAME',
    placeholderKey: 'ORGCOMPANIES_PH_NAME',
    required: true,
    span: 2,
  },
  {
    key: 'website',
    labelKey: 'ORGCOMPANIES_FIELD_WEBSITE',
    hintKey: 'ORGCOMPANIES_HINT_WEBSITE',
    placeholderKey: 'ORGCOMPANIES_PH_WEBSITE',
    type: 'url',
    span: 2,
  },
  {
    key: 'email',
    labelKey: 'ORGCOMPANIES_FIELD_EMAIL',
    hintKey: 'ORGCOMPANIES_HINT_EMAIL',
    placeholderKey: 'ORGCOMPANIES_PH_EMAIL',
    type: 'email',
    span: 1,
  },
  {
    key: 'phone',
    labelKey: 'ORGCOMPANIES_FIELD_PHONE',
    hintKey: 'ORGCOMPANIES_HINT_PHONE',
    placeholderKey: 'ORGCOMPANIES_PH_PHONE',
    type: 'tel',
    span: 1,
  },
  {
    key: 'address',
    labelKey: 'ORGCOMPANIES_FIELD_ADDRESS',
    hintKey: 'ORGCOMPANIES_HINT_ADDRESS',
    placeholderKey: 'ORGCOMPANIES_PH_ADDRESS',
    span: 2,
    multiline: true,
  },
];

type FieldKey = FieldDef['key'];
type FormState = Record<FieldKey, string>;

export interface CompanyFormProps {
  /** When provided the form edits this company; otherwise it creates a new one. */
  company?: Company | null;
  /** Prefill values for create mode (e.g. the name typed in the company picker). */
  defaults?: CompanyDraft;
  /** Seed the "share with org" checkbox for new companies. */
  defaultShareWithOrg?: boolean;
  /** Hide the "share with org" checkbox (forces a personal company). */
  canShareWithOrg?: boolean;
  orgName?: string;
  /** Fired after a successful create with the new company. */
  onCreated?: (company: Company) => void;
  /** Fired on cancel and after a successful save — close the dialog, or return to the caller's view. */
  onDone: () => void;
  bodyClassName?: string;
  footerClassName?: string;
}

/**
 * The company create/edit fields, validation and submit — WITHOUT any dialog chrome.
 *
 * Kept separate from `CompanyFormDialog` so the same form can render inline inside
 * another dialog (the contact form swaps to it rather than stacking a second dialog).
 * State is initialised on mount, so callers reseed by mounting/unmounting.
 */
export function CompanyForm({
  company,
  defaults,
  defaultShareWithOrg = false,
  canShareWithOrg = true,
  orgName,
  onCreated,
  onDone,
  bodyClassName,
  footerClassName,
}: CompanyFormProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const orgUDSId = useAuthStore((s) => s.orgUDSId);
  const { mutateAsync: createCompany, isPending: isCreating } = useCreateCompany();
  const { mutateAsync: updateCompany, isPending: isUpdating } = useUpdateCompany();

  const isEdit = !!company;
  const isBusy = isCreating || isUpdating;

  const [form, setForm] = useState<FormState>(() => ({
    name: company?.name ?? defaults?.name ?? '',
    website: company?.website ?? defaults?.website ?? '',
    email: company?.email ?? defaults?.email ?? '',
    phone: company?.phone ?? defaults?.phone ?? '',
    address: company?.address ?? defaults?.address ?? '',
  }));
  const [errors, setErrors] = useState<Partial<Record<FieldKey, string>>>({});
  const [shareWithOrg, setShareWithOrg] = useState(defaultShareWithOrg);

  const set = (key: FieldKey, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
  };

  const validate = (): boolean => {
    const next: Partial<Record<FieldKey, string>> = {};
    if (!form.name.trim()) next.name = t('ORGCOMPANIES_NAME_REQUIRED');
    const website = form.website.trim();
    if (website && !isValidWebsite(website)) next.website = t('ORGCOMPANIES_INVALID_WEBSITE');
    const email = form.email.trim();
    if (email && !EMAIL_RE.test(email)) next.email = t('ORGCOMPANIES_INVALID_EMAIL');
    const phone = form.phone.trim();
    if (phone && !PHONE_RE.test(phone)) next.phone = t('ORGCOMPANIES_INVALID_PHONE');
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = async () => {
    if (!validate()) return;

    const trimmed = {
      Name: form.name.trim(),
      Website: form.website.trim(),
      Address: form.address.trim(),
      Email: form.email.trim(),
      Phone: form.phone.trim(),
    };

    try {
      if (isEdit && company) {
        // Sent as-is (including empty strings) so clearing an optional field persists.
        const input: CompanyUpdateInput = { ...trimmed };
        await updateCompany({ itemId: company.id, input });
      } else {
        if (shareWithOrg && !orgUDSId) {
          toast({ variant: 'destructive', title: t('ORGCOMPANIES_SHARE_NO_ORG') });
          return;
        }
        const scope = shareWithOrg ? 'organization' : 'user';
        const input: CompanyInsertInput = {
          Scope: scope,
          Name: trimmed.Name,
          Website: trimmed.Website || undefined,
          Address: trimmed.Address || undefined,
          Email: trimmed.Email || undefined,
          Phone: trimmed.Phone || undefined,
          ...(scope === 'organization' && orgUDSId
            ? { OrganizationId: orgUDSId, OrganizationIds: [orgUDSId] }
            : {}),
        };
        const id = await createCompany(input);
        toast({ variant: 'success', title: t('ORGCOMPANIES_CREATED') });
        onCreated?.({
          id,
          scope,
          name: trimmed.Name,
          website: trimmed.Website,
          address: trimmed.Address,
          email: trimmed.Email,
          phone: trimmed.Phone,
          organizationId: scope === 'organization' ? (orgUDSId ?? undefined) : undefined,
          canManage: true,
        });
      }
      onDone();
    } catch {
      /* mutation hooks surface the error toast */
    }
  };

  return (
    <>
      {/* Scrolling is owned by the surrounding dialog, so this is plain flow. */}
      <div className={cn('space-y-3', bodyClassName)}>
        <div className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2">
          {FIELD_DEFS.map((field) => {
            const error = errors[field.key];
            const describedBy = error
              ? `company-${field.key}-error`
              : `company-${field.key}-hint`;
            return (
              <div
                key={field.key}
                className={cn('space-y-1', field.span === 2 && 'sm:col-span-2')}
              >
                <Label htmlFor={`company-${field.key}`} className="text-xs font-medium">
                  {t(field.labelKey)}
                  {field.required && <span className="text-crimson"> *</span>}
                </Label>

                {field.multiline ? (
                  <Textarea
                    id={`company-${field.key}`}
                    rows={3}
                    value={form[field.key]}
                    placeholder={t(field.placeholderKey)}
                    onChange={(e) => set(field.key, e.target.value)}
                    aria-invalid={!!error}
                    aria-describedby={describedBy}
                    className="resize-none"
                  />
                ) : (
                  <Input
                    id={`company-${field.key}`}
                    type={field.type ?? 'text'}
                    value={form[field.key]}
                    placeholder={t(field.placeholderKey)}
                    onChange={(e) => set(field.key, e.target.value)}
                    aria-invalid={!!error}
                    aria-describedby={describedBy}
                    className={cn('h-9', error && 'border-crimson focus-visible:ring-crimson')}
                  />
                )}

                {error ? (
                  <p id={`company-${field.key}-error`} className="text-xs text-crimson">
                    {error}
                  </p>
                ) : (
                  <p id={`company-${field.key}-hint`} className="text-xs text-globe-grey">
                    {t(field.hintKey)}
                  </p>
                )}
              </div>
            );
          })}
        </div>

        {isEdit ? (
          <p className="rounded-md bg-muted/50 px-3 py-2 text-xs text-globe-grey mt-2">
            {company?.scope === 'organization' ? (
              <Trans
                i18nKey="ORGCOMPANIES_SCOPE_NOTE_ORG"
                values={{ org: orgName || t('ORGCOMPANIES_YOUR_ORG') }}
                components={{ b: <span className="font-semibold text-selise-blue" /> }}
              />
            ) : (
              t('ORGCOMPANIES_SCOPE_NOTE_PERSONAL')
            )}
          </p>
        ) : canShareWithOrg ? (
          <label className="flex items-center gap-2 text-sm cursor-pointer mt-2">
            <Checkbox checked={shareWithOrg} onCheckedChange={(v) => setShareWithOrg(v === true)} />
            <span>
              <Trans
                i18nKey="ORGCOMPANIES_SHARE_LABEL"
                values={{ org: orgName || t('ORGCOMPANIES_YOUR_ORG') }}
                components={{ b: <span className="font-semibold text-selise-blue" /> }}
              />
            </span>
          </label>
        ) : null}
      </div>

      <div className={cn('flex flex-row justify-end gap-4', footerClassName)}>
        <Button variant="ghost" onClick={onDone} disabled={isBusy}>
          {t('CANCEL')}
        </Button>
        <Button variant="primary" onClick={submit} disabled={isBusy}>
          {isBusy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
          {isEdit ? t('ORGCOMPANIES_SAVE_CTA') : t('ORGCOMPANIES_CREATE_CTA')}
        </Button>
      </div>
    </>
  );
}

export default CompanyForm;
